import React from 'react'
import { Link } from 'react-router-dom'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import Pill from 'components/Pill'
import {
  DateCell,
  ImageCell,
  TableCell,
  TableRow,
  TextCell,
} from 'components/Table'
import Document from 'documents/Document'
import link from 'infra/link'
import { Id } from 'types/definitions'
import { pluralize } from 'utils/pluralization-util'
import { ColumnId } from '../DocumentTableColumns'
import { DocumentManagementViewModel } from '../DocumentManagementViewModel'
import styles from './DocumentManagementTableRow.sass'

interface DocumentManagementTableRowProps {
  vm: DocumentManagementViewModel
  document: Document
}

export default observer(function DocumentManagementTableRow({
  vm,
  document,
}: DocumentManagementTableRowProps) {
  return (
    <TableRow data-test={`document-management-table-row-${document.id}`}>
      <DocumentManagementTableCell vm={vm} document={document} />
    </TableRow>
  )
})

interface DocumentManagementTableCellProps {
  vm: DocumentManagementViewModel
  document: Document
}

export const DocumentManagementTableCell = observer(
  ({ vm, document }: DocumentManagementTableCellProps) => {
    const columns = vm.columns
    const linkedJobs = document.linkedJobs

    return columns.map((column) => {
      switch (column.id) {
        case ColumnId.Checkbox:
          return (
            <TableCell key={column.id}>
              <div>
                <Checkbox
                  muteClickEvent
                  checked={vm.isSelected(document.id)}
                  onChange={(checked) =>
                    vm.toggleDocument(checked, document.id)
                  }
                  data-test={`document-management-table-row-checkbox-${document.id}`}
                />
              </div>
            </TableCell>
          )
        case ColumnId.DocumentTitle:
          return (
            <TextCell
              key={column.id}
              dataTest={`document-management-table-row-name-${document.id}`}
            >
              <div className={styles.titleContainer}>
                <span>{document.title}</span>
                {document.archived && (
                  <span className={styles.pillContainer}>
                    <Pill color="red" shape="square" trim>
                      Archived
                    </Pill>
                  </span>
                )}
              </div>
            </TextCell>
          )
        case ColumnId.Workspace:
          return (
            <ImageCell
              title={document.workspace.name}
              image={document.workspace.branding.logos.icon}
              dataTest={`document-management-table-row-workspace-${document.id}`}
              key={column.id}
            />
          )
        case ColumnId.Owner:
          return (
            <ImageCell
              title={document.owner.displayName}
              image={document.owner.avatar}
              dataTest={`document-management-table-row-owner-${document.id}`}
              key={column.id}
            />
          )
        case ColumnId.Type:
          return (
            <TextCell
              key={column.id}
              dataTest={`document-management-table-row-type-${document.id}`}
            >
              {document.fileType !== 'file'
                ? document.fileType.toUpperCase()
                : (document.title
                    .match(/\.\w+$/)
                    ?.join()
                    .replace('.', '')
                    .toUpperCase() ?? 'Document')}
            </TextCell>
          )
        case ColumnId.DateModified:
          return (
            <DateCell
              date={document.dateModified}
              dataTest={`document-management-table-row-date-modified-${document.id}`}
              key={column.id}
            />
          )
        case ColumnId.LinkedJob:
          return (
            <TableCell key={column.id}>
              <Dropdown
                autoDismiss
                position="bottom-end"
                disabled={linkedJobs.length < 1}
                dataTest={`document-management-table-row-linked-job-dropdown-${document.id}`}
                button={
                  <Button type="secondary">
                    {`${linkedJobs.length} linked ${pluralize('job', linkedJobs.length)}`}
                  </Button>
                }
              >
                {linkedJobs.map((job) => (
                  <DropdownItem
                    key={job.id}
                    icon={<Icon name="link" color="var(--grey600)" />}
                    muteClickEvent
                  >
                    <Link
                      className={styles.link}
                      to={link.job(Id.assert(job.id))}
                      data-test={`document-management-table-row-linked-job-dropdown-link-${job.id}`}
                    >
                      <div className={styles.linkInfoContainer}>
                        {job.displayName && (
                          <span className={styles.linkInfoTitle}>
                            {job.displayName}
                          </span>
                        )}
                        <span className={styles.linkInfoSubtitle}>
                          {job.id}
                        </span>
                      </div>
                    </Link>
                  </DropdownItem>
                ))}
              </Dropdown>
            </TableCell>
          )
        case ColumnId.Action:
          return (
            <TableCell key={column.id}>
              <Dropdown
                autoDismiss
                position="bottom-end"
                dataTest={`document-management-table-row-actions-${document.id}`}
                button={
                  <IconButton>
                    <Icon name="more" color="var(--grey600)" />
                  </IconButton>
                }
              >
                <DropdownTitle>Actions</DropdownTitle>
                <DropdownItem
                  onClick={() => vm.downloadDocuments(document)}
                  icon={<Icon name="download" color="var(--grey600)" />}
                  dataTest="document-management-table-row-actions-download"
                  muteClickEvent
                >
                  Download
                </DropdownItem>
                <DropdownItem
                  onClick={() => vm.showDocumentDetails(document)}
                  icon={<Icon name="edit-outlined" color="var(--grey600)" />}
                  dataTest="document-management-table-row-actions-details"
                  muteClickEvent
                >
                  Details
                </DropdownItem>
                {!document.archived && (
                  <DropdownItem
                    onClick={() => vm.archiveDocuments(document)}
                    icon={<Icon name="delete" color="var(--grey600)" />}
                    dataTest="document-management-table-row-actions-delete"
                    muteClickEvent
                  >
                    Delete
                  </DropdownItem>
                )}
              </Dropdown>
            </TableCell>
          )
        default:
          return <TableCell key={column.id}>{null}</TableCell>
      }
    })
  },
)
